/**
 * @module useBaggage
 */
import { useCallback } from 'react'
import {
  useGetBaggageListMutation,
  useCreateBaggageMutation,
  useGetBaggageMutation,
  useUpdateBaggageMutation,
  useDeleteBaggageMutation,
  useUpdateBaggageStatusMutation,
  useAssignBaggageToFlightMutation,
  useAssignBaggageToPassengerMutation,
  useGetBaggageByPassengerMutation,
  useGetBaggageByFlightMutation,
} from '../services/baggage.service'

export const useBaggage = () => {
  const [executeGetBaggageList, { isLoading: isGetBaggageListLoading }] =
    useGetBaggageListMutation()
  const [executeCreateBaggage, { isLoading: isCreateBaggageLoading }] = useCreateBaggageMutation()
  const [executeGetBaggage, { isLoading: isGetBaggageLoading }] = useGetBaggageMutation()
  const [executeUpdateBaggage, { isLoading: isUpdateBaggageLoading }] = useUpdateBaggageMutation()
  const [executeDeleteBaggage, { isLoading: isDeleteBaggageLoading }] = useDeleteBaggageMutation()
  const [executeUpdateBaggageStatus, { isLoading: isUpdateBaggageStatusLoading }] =
    useUpdateBaggageStatusMutation()
  const [executeAssignBaggageToFlight, { isLoading: isAssignBaggageToFlightLoading }] =
    useAssignBaggageToFlightMutation()
  const [executeAssignBaggageToPassenger, { isLoading: isAssignBaggageToPassengerLoading }] =
    useAssignBaggageToPassengerMutation()
  const [executeGetBaggageByPassenger, { isLoading: isGetBaggageByPassengerLoading }] =
    useGetBaggageByPassengerMutation()
  const [executeGetBaggageByFlight, { isLoading: isGetBaggageByFlightLoading }] =
    useGetBaggageByFlightMutation()

  const getBaggageListAsync = useCallback(async () => {
    if (isGetBaggageListLoading) {
      return
    }
    try {
      const response = await executeGetBaggageList().unwrap()
      return response.data.baggages
    } catch (error) {
      console.error('#getBaggageListAsync: Error fetching baggage list', error)
      throw error
    }
  }, [executeGetBaggageList, isGetBaggageListLoading])

  const createBaggageAsync = useCallback(
    async (baggageData) => {
      if (isCreateBaggageLoading) {
        return
      }
      try {
        const response = await executeCreateBaggage(baggageData).unwrap()
        return response.data.baggage
      } catch (error) {
        console.error('#createBaggageAsync: Error creating baggage', error)
        throw error
      }
    },
    [executeCreateBaggage, isCreateBaggageLoading],
  )

  const getBaggageAsync = useCallback(
    async (baggageId) => {
      if (isGetBaggageLoading) {
        return
      }
      try {
        const response = await executeGetBaggage(baggageId).unwrap()
        return response.data.baggage
      } catch (error) {
        console.error('#getBaggageAsync: Error fetching baggage', error)
        throw error
      }
    },
    [executeGetBaggage, isGetBaggageLoading],
  )

  const updateBaggageAsync = useCallback(
    async (baggageId, updates) => {
      if (isUpdateBaggageLoading) {
        return
      }
      try {
        const response = await executeUpdateBaggage({ baggageId, ...updates }).unwrap()
        return response.data.baggage
      } catch (error) {
        console.error('#updateBaggageAsync: Error updating baggage', error)
        throw error
      }
    },
    [executeUpdateBaggage, isUpdateBaggageLoading],
  )

  const deleteBaggageAsync = useCallback(
    async (baggageId) => {
      if (isDeleteBaggageLoading) {
        return
      }
      try {
        const response = await executeDeleteBaggage(baggageId).unwrap()
        return response.data
      } catch (error) {
        console.error('#deleteBaggageAsync: Error deleting baggage', error)
        throw error
      }
    },
    [executeDeleteBaggage, isDeleteBaggageLoading],
  )

  const updateBaggageStatusAsync = useCallback(
    async (baggageId, status) => {
      if (isUpdateBaggageStatusLoading) {
        return
      }
      try {
        const response = await executeUpdateBaggageStatus({ baggageId, status }).unwrap()
        return response.data.baggage
      } catch (error) {
        console.error('#updateBaggageStatusAsync: Error updating baggage status', error)
        throw error
      }
    },
    [executeUpdateBaggageStatus, isUpdateBaggageStatusLoading],
  )

  const assignBaggageToFlightAsync = useCallback(
    async (baggageId, flightId) => {
      if (isAssignBaggageToFlightLoading) {
        return
      }
      try {
        const response = await executeAssignBaggageToFlight({ baggageId, flightId }).unwrap()
        return response.data.baggage
      } catch (error) {
        console.error('#assignBaggageToFlightAsync: Error assigning baggage to flight', error)
        throw error
      }
    },
    [executeAssignBaggageToFlight, isAssignBaggageToFlightLoading],
  )

  const assignBaggageToPassengerAsync = useCallback(
    async (baggageId, passengerId) => {
      if (isAssignBaggageToPassengerLoading) {
        return
      }
      try {
        const response = await executeAssignBaggageToPassenger({ baggageId, passengerId }).unwrap()
        return response.data.baggage
      } catch (error) {
        console.error('#assignBaggageToPassengerAsync: Error assigning baggage to passenger', error)
        throw error
      }
    },
    [executeAssignBaggageToPassenger, isAssignBaggageToPassengerLoading],
  )

  const getBaggageByPassengerAsync = useCallback(
    async (passengerId, params = {}) => {
      if (isGetBaggageByPassengerLoading) {
        return
      }
      try {
        const response = await executeGetBaggageByPassenger({ passengerId, ...params }).unwrap()
        return response.data.baggage
      } catch (error) {
        console.error('#getBaggageByPassengerAsync: Error fetching baggage by passenger', error)
        throw error
      }
    },
    [executeGetBaggageByPassenger, isGetBaggageByPassengerLoading],
  )

  const getBaggageByFlightAsync = useCallback(
    async (flightId, params = {}) => {
      if (isGetBaggageByFlightLoading) {
        return
      }
      try {
        const response = await executeGetBaggageByFlight({ flightId, ...params }).unwrap()
        return response.data.baggage
      } catch (error) {
        console.error('#getBaggageByFlightAsync: Error fetching baggage by flight', error)
        throw error
      }
    },
    [executeGetBaggageByFlight, isGetBaggageByFlightLoading],
  )

  return {
    getBaggageListAsync,
    createBaggageAsync,
    getBaggageAsync,
    updateBaggageAsync,
    deleteBaggageAsync,
    updateBaggageStatusAsync,
    assignBaggageToFlightAsync,
    assignBaggageToPassengerAsync,
    getBaggageByPassengerAsync,
    getBaggageByFlightAsync,
    isGetBaggageListLoading,
    isCreateBaggageLoading,
    isGetBaggageLoading,
    isUpdateBaggageLoading,
    isDeleteBaggageLoading,
    isUpdateBaggageStatusLoading,
    isAssignBaggageToFlightLoading,
    isAssignBaggageToPassengerLoading,
    isGetBaggageByPassengerLoading,
    isGetBaggageByFlightLoading,
  }
}
